// src/components/login/mfa-setup-page.tsx
//
// -- Migrated onto the kit, Phase 6 ------------------------------------------
//
// Three phases on one auth surface. `ui/button`, `ui/input` and `ui/alert` are
// gone; `AuthCard`, `Button`, `TextInput`, `Banner`, `CopyButton` and `Spinner`
// are in.
//
// What did NOT move: `authApi.mfaSetupInit`, `authApi.mfaSetupVerify`, the
// three phases, the clipboard and download helpers, and every string.
//
// Two things the swap fixed:
//
//   * The copy control was a bare <button> with an inline style and a
//     hand-rolled 2000ms confirmation timer. `CopyButton` reads its hold out
//     of `--d-confirm`, so the glyph reverts on the same beat the box cools.
//   * The QR panel forced a literal white ground so the code would scan. It is
//     `--s-raised` with an explicit white plate under the SVG only, which is
//     the part that actually has to be white.
import { useEffect, useState, FormEvent } from "react";
import { QRCodeSVG } from "qrcode.react";
import { authApi } from "@/api/auth";
import { UpmodeLogo } from "./upmode-logo";
import { ShieldCheck, Download } from "lucide-react";
import {
  AuthCard, Banner, Button, CopyButton, FieldRow, FormStack, Icon,
  InlineLink, Spinner, TextInput,
} from "../kit";

interface MfaSetupPageProps {
  setupToken: string;
  username: string;
  onSetupComplete: (accessToken: string, payload: {
    username: string;
    is_admin: boolean;
    user_type: string;
    full_name?: string | null;
  }) => void;
  rememberMe?: boolean;
}

type Phase = "qr" | "verify" | "backup_codes";

export function MfaSetupPage({
  setupToken,
  username,
  onSetupComplete,
}: MfaSetupPageProps) {
  const [phase, setPhase] = useState<Phase>("qr");

  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [secretB32, setSecretB32]   = useState<string | null>(null);
  const [code, setCode]             = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [pendingAccessToken, setPendingAccessToken] = useState<string | null>(null);
  const [pendingPayload, setPendingPayload] = useState<any>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg,  setErrorMsg]  = useState("");

  // ─── Step 1: fetch the QR / secret on mount ───
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authApi.mfaSetupInit(setupToken);
        if (cancelled) return;
        setOtpauthUri(res.otpauth_uri);
        setSecretB32(res.secret_b32);
        setIsLoading(false);
      } catch (e: any) {
        if (cancelled) return;
        setErrorMsg(e?.message || "Could not start MFA setup. Try logging in again.");
        setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupToken]);

  // ─── Step 2: verify the first TOTP code ───
  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMsg("");

    const cleaned = code.replace(/\D/g, "");
    if (cleaned.length !== 6) {
      setErrorMsg("Enter the 6-digit code from your authenticator app.");
      return;
    }

    setIsLoading(true);
    try {
      const res = await authApi.mfaSetupVerify(setupToken, cleaned);
      setBackupCodes(res.backup_codes || []);
      setPendingAccessToken(res.access_token);
      setPendingPayload({
        username:  res.username,
        is_admin:  res.is_admin,
        user_type: res.user_type || "basic",
        full_name: res.full_name,
      });
      setPhase("backup_codes");
    } catch (e: any) {
      setErrorMsg(
        /invalid code/i.test(String(e?.message))
          ? "That code didn't match. Try the latest 6-digit code from your app."
          : (e?.message || "Verification failed.")
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinish = () => {
    if (pendingAccessToken && pendingPayload) {
      onSetupComplete(pendingAccessToken, pendingPayload);
    }
  };

  const downloadBackupCodes = () => {
    const text =
      `Upmode AI — backup codes for ${username}\n` +
      `Generated: ${new Date().toISOString()}\n\n` +
      `Each code can be used once if you lose access to your authenticator app.\n` +
      `Keep these somewhere safe (password manager, encrypted note).\n\n` +
      backupCodes.join("\n") + "\n";
    const blob = new Blob([text], { type: "text/plain" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url;
    a.download = `upmode-backup-codes-${username}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const title = phase === "backup_codes"
    ? "Save your backup codes"
    : "Set up two-factor authentication";
  const description = phase === "backup_codes"
    ? "These let you sign in if you lose access to your authenticator."
    : `Required for ${username}. Takes about 60 seconds.`;

  return (
    <div className="upm-auth-page" data-kit>
      <div className="upm-auth-page__wash" aria-hidden="true" />

      <div className="upm-auth-page__column upm-auth-page__column--wide">
        <div className="upm-auth-page__brand">
          <UpmodeLogo />
        </div>

        <AuthCard title={title} description={description} className="upm-auth-page__card--wide">
          {errorMsg ? <Banner tone="error">{errorMsg}</Banner> : null}

          {/* PHASE: QR + secret */}
          {phase === "qr" && (
            isLoading ? (
              <div className="upm-auth-page__waiting">
                <Spinner label="Generating your secret" />
              </div>
            ) : otpauthUri ? (
              <FormStack>
                <ol className="upm-auth-page__steps">
                  <li>Open Google Authenticator, 1Password, Authy, or your password manager.</li>
                  <li>Scan this QR code, or paste the secret manually.</li>
                  <li>Enter the 6-digit code it shows you.</li>
                </ol>

                {/* The plate is white because a QR code has to be scannable, and
                    that is a property of the symbol rather than of the theme. It
                    is the only white in the page and it is the size of the code. */}
                <div className="upm-auth-page__qr">
                  <QRCodeSVG value={otpauthUri} size={180} level="M" />
                </div>

                {secretB32 && (
                  <div className="upm-auth-page__secret">
                    <code className="upm-auth-page__secret-value">{secretB32}</code>
                    <CopyButton value={secretB32} label="Copy secret" confirmedLabel="Secret copied" />
                  </div>
                )}

                <Button
                  type="button"
                  onClick={() => setPhase("verify")}
                  className="upm-auth-page__submit"
                >
                  I have added it, continue
                </Button>
              </FormStack>
            ) : null
          )}

          {/* PHASE: enter first TOTP */}
          {phase === "verify" && (
            <form onSubmit={handleVerify}>
              <FormStack>
                <p className="upm-auth-page__hint">
                  <Icon icon={ShieldCheck} size="md" />
                  Enter the 6-digit code from your authenticator app.
                </p>

                <FieldRow label="Authentication code" required>
                  {({ id }) => (
                    <TextInput
                      id={id}
                      className="upm-auth-page__code"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="123456"
                      autoFocus
                      state={isLoading ? "disabled" : undefined}
                    />
                  )}
                </FieldRow>

                <Button
                  type="submit"
                  disabled={isLoading || code.length !== 6}
                  loading={isLoading}
                  loadingLabel="Verifying"
                  className="upm-auth-page__submit"
                >
                  Verify and finish
                </Button>

                <p className="upm-auth-page__legal">
                  <InlineLink
                    href="#"
                    onClick={(e) => { e.preventDefault(); setPhase("qr"); setCode(""); setErrorMsg(""); }}
                  >
                    Back to QR code
                  </InlineLink>
                </p>
              </FormStack>
            </form>
          )}

          {/* PHASE: show backup codes */}
          {phase === "backup_codes" && (
            <FormStack>
              <Banner tone="ok">Two-factor authentication is now active.</Banner>

              <p className="upm-auth-page__body">
                Save these <strong>backup codes</strong> now. Each works once if you lose your
                authenticator. They will not be shown again.
              </p>

              <div className="upm-auth-page__codes">
                {backupCodes.map((c, i) => (
                  <code key={i}>{c}</code>
                ))}
              </div>

              <div className="upm-auth-page__row">
                <CopyButton
                  value={backupCodes.join("\n")}
                  label="Copy backup codes"
                  confirmedLabel="Backup codes copied"
                />
                <Button type="button" variant="secondary" icon={Download} onClick={downloadBackupCodes}>
                  Download .txt
                </Button>
              </div>

              <Button type="button" onClick={handleFinish} className="upm-auth-page__submit">
                I have saved them, continue to Upmode
              </Button>
            </FormStack>
          )}
        </AuthCard>

        <p className="upm-auth-page__legal">
          Lost your authenticator? Contact your administrator to reset MFA.
        </p>
      </div>
    </div>
  );
}
